import { Box, Text, Image } from 'theme-ui';

import { Section, Svg } from '@snippets';

import { Schema } from './ProductComparison.schema';
import { themed } from './ProductComparison.theme';

function StatusIcon({ status, theme, bgColor }) {
  const getIconStyle = () => {
    switch (status) {
      case 'check':
        return theme.checkIcon;
      case 'cross':
        return theme.crossIcon;
      case 'partial':
        return theme.partialIcon;
      default:
        return theme.crossIcon;
    }
  };

  const getIconContent = () => {
    switch (status) {
      case 'check':
        return '✓';
      case 'cross':
        return '✕';
      case 'partial':
        return '◐';
      default:
        return '✕';
    }
  };

  if (status === 'check') {
    return (
      <Svg
        alt="checkmark"
        src="/svgs/noprecache/svg/checkmark.svg#checkmark"
        viewBox="0 0 40 40"
        className={`w-[32px] h-[32px] ${
          bgColor === '#2c2c2c' ? 'text-[#E1FF05]' : 'text-[#222222] opacity-50'
        } `}
      />
    );
  }
  if (status === 'cross') {
    return (
      <Svg
        alt="cross mark"
        src="/svgs/noprecache/svg/cross-mark.svg#cross-mark"
        viewBox="0 0 40 40"
        className={`w-[32px] h-[32px] ${
          bgColor === '#2c2c2c' ? 'text-[#E1FF05]' : 'text-[#222222] opacity-50'
        } `}
      />
    );
  }

  return <Box sx={getIconStyle()}>{getIconContent()}</Box>;
}

export const ProductComparison = themed(({ theme, cms }) => {
  const { text, brands, comparisonItems, section } = cms;
  const yourBrandBgColor = brands?.yourBrandBgColor || '#2c2c2c';
  const competitorBgColor = brands?.competitorBgColor || '#f5f5f5';

  return (
    <Section section={section}>
      <Box data-comp={ProductComparison.displayName} sx={theme.wrapper}>
        {text?.heading && (
          <Text
            as="h2"
            sx={{
              ...theme.heading,
              textAlign: text?.textAlign,
              fontFamily: 'Reckless Neue',
              fontSize: [10, 13],
              fontWeight: 400,
              mb: [12, 16],
            }}
          >
            {text.heading}
          </Text>
        )}

        <Box sx={theme.comparisonTable} className="comparison-table">
          {/* Header Row */}
          <Box sx={theme.headerRow} className="header-row">
            <Box sx={theme.brandHeader}>
              {/* Empty cell for feature column */}
            </Box>
            <Box
              sx={{
                ...theme.brandHeader,
                bg: yourBrandBgColor,
                color: yourBrandBgColor === '#2c2c2c' ? 'white' : 'dark',
              }}
            >
              {brands?.yourBrandImage?.src ? (
                <Image
                  src={brands.yourBrandImage.src}
                  alt={brands?.yourBrand || 'Your Brand'}
                  sx={theme.brandImage}
                />
              ) : (
                <Box
                  sx={{
                    ...theme.brandImage,
                    bg: 'white',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text sx={{ fontSize: 2, fontWeight: 'bold', color: 'dark' }}>
                    {(brands?.yourBrand || 'BrüMate').charAt(0)}
                  </Text>
                </Box>
              )}
              <Text sx={theme.brandName}>{brands?.yourBrand || 'BrüMate'}</Text>
            </Box>
            <Box
              sx={{
                ...theme.brandHeader,
                bg: competitorBgColor,
                color: competitorBgColor === '#2c2c2c' ? 'white' : 'dark',
              }}
            >
              {brands?.competitorBrandImage?.src ? (
                <Image
                  src={brands.competitorBrandImage.src}
                  alt={brands?.competitorBrand || 'Competitor'}
                  sx={theme.brandImage}
                />
              ) : (
                <Box
                  sx={{
                    ...theme.brandImage,
                    bg: 'white',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text sx={{ fontSize: 2, fontWeight: 'bold', color: 'dark' }}>
                    {(brands?.competitorBrand || 'Others').charAt(0)}
                  </Text>
                </Box>
              )}
              <Text sx={theme.brandName}>
                {brands?.competitorBrand || 'Others'}
              </Text>
            </Box>
          </Box>

          {/* Comparison Rows */}
          {comparisonItems?.map((item, index) => (
            <Box
              key={item.id || index}
              sx={theme.comparisonRow}
              className="comparison-row"
            >
              <Box sx={theme.featureCell} className="feature-cell">
                <Text sx={theme.featureTitle}>{item.title}</Text>
                {item.description && (
                  <Text sx={theme.featureDescription}>{item.description}</Text>
                )}
              </Box>
              <Box
                sx={{ ...theme.statusCell, bg: yourBrandBgColor }}
                className="status-cell"
              >
                <StatusIcon
                  status={item.yourBrandStatus}
                  theme={theme}
                  bgColor={yourBrandBgColor}
                />
                <Text sx={{ mt: 1, fontSize: 0 }} className="sr-only">
                  {brands?.yourBrand || 'Your Brand'}
                </Text>
              </Box>
              <Box
                sx={{ ...theme.statusCell, bg: competitorBgColor }}
                className="status-cell"
              >
                <StatusIcon
                  status={item.competitorStatus}
                  theme={theme}
                  bgColor={competitorBgColor}
                />
                <Text sx={{ mt: 1, fontSize: 0 }} className="sr-only">
                  {brands?.competitorBrand || 'Others'}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
});

ProductComparison.displayName = 'ProductComparison';
ProductComparison.Schema = Schema;
